import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { RATING, cosmeticWordKey } from '@il-colpo/meta/progress';
import { useLooseT } from '../../game/text/loose.ts';
import type { SettlementSummary } from '@il-colpo/protocol';
import { useFlag } from '../../meta/liveStore.ts';
import { useConnection } from '../../online/onlineConnection.ts';
import { Badge, Panel } from '../../ui/index.ts';
import { useChallengeTitle } from './common.tsx';
import styles from './Meta.module.css';

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const XP_LINES = [
  'participation',
  'success',
  'win',
  'unmask',
  'infiltratorWin',
  'firstOfDay',
] as const;

/**
 * What the heist did for the player, as the server told them once it had settled it: the XP and where
 * it came from, the level, the challenges, the trophies and cosmetics, the rating of a ranked game.
 * Nothing is shown for a game that is not online, or before the server has said anything.
 */
export function SettlementPanel() {
  const { settlement } = useConnection();
  return settlement ? <SettlementView summary={settlement} /> : null;
}

/** The summary itself (the design gallery shows it with a sample). */
export function SettlementView({ summary: s }: { summary: SettlementSummary }) {
  const { t } = useTranslation('meta');
  const tm = useLooseT('meta');
  const challengeName = useChallengeTitle();
  const replaysOn = useFlag('replays');
  if (s.xpGain === 0 && s.challenges.length === 0 && s.trophies.length === 0) {
    return (
      <Panel tone="night" title={t('settlement.title')}>
        <p className={styles['note']}>{t('settlement.none')}</p>
      </Panel>
    );
  }
  return (
    <Panel tone="night" title={t('settlement.title')}>
      <div className={styles['settlement']} data-testid="settlement">
        <span className={styles['gain']}>{t('settlement.xp', { xp: s.xpGain })}</span>
        <span className={styles['note']}>
          {s.levelAfter > s.levelBefore
            ? t('settlement.levelUp', { level: s.levelAfter })
            : t('settlement.level', { level: s.levelAfter })}
        </span>
        <dl className={styles['breakdown']} aria-label={t('settlement.breakdown')}>
          {XP_LINES.filter((k) => s.xp[k] > 0).map((k) => (
            <div key={k} style={{ display: 'contents' }}>
              <dt>{tm(`settlement.${k}`)}</dt>
              <dd>{s.xp[k]}</dd>
            </div>
          ))}
        </dl>
        {s.challenges.some((c) => c.completed) ? (
          <div>
            <h3 className={styles['subtitle']}>{t('settlement.challenges')}</h3>
            <ul className={styles['list']}>
              {s.challenges
                .filter((c) => c.completed)
                .map((c) => (
                  <li key={c.id} className={styles['note']}>
                    {`${challengeName(c.id)} — ${t('settlement.challengeDone', { xp: c.rewardXp })}`}
                  </li>
                ))}
            </ul>
          </div>
        ) : null}
        {s.trophies.length > 0 ? (
          <div>
            <h3 className={styles['subtitle']}>{t('settlement.trophies')}</h3>
            <div className={styles['pills']}>
              {s.trophies.map((id) => (
                <Badge key={id} tone="amber">
                  {tm(`trophy.${id}.name`)}
                </Badge>
              ))}
            </div>
          </div>
        ) : null}
        {s.cosmetics.length > 0 ? (
          <div>
            <h3 className={styles['subtitle']}>{t('settlement.cosmetics')}</h3>
            <div className={styles['pills']}>
              {s.cosmetics.map((id) => (
                <Badge key={id} tone="cyan">
                  {tm(`cosmetic.${cosmeticWordKey(id)}`)}
                </Badge>
              ))}
            </div>
          </div>
        ) : null}
        {s.rating ? (
          <p className={styles['note']}>
            {s.rating.placed
              ? t('settlement.rating', {
                  before: s.rating.displayBefore,
                  after: s.rating.displayAfter,
                })
              : t('settlement.placing', { games: s.rating.games, total: RATING.placementGames })}
          </p>
        ) : null}
        <div className={styles['row']}>
          <Link to="/progress">{t('settlement.toRapSheet')}</Link>
          {replaysOn && UUID.test(s.matchId) ? (
            <Link to={`/replay/${s.matchId}`}>{t('settlement.replay')}</Link>
          ) : null}
        </div>
      </div>
    </Panel>
  );
}
